import type {
  AuthMeResponse,
  CrestPalette,
  CrestSigil,
  EmailPreferences,
  ChatHistoryResponse,
  ChatReportReceipt,
  GuildAcceptInvitationResponse,
  GuildCallProjection,
  GuildCreateInput,
  GuildCreateInvitationResponse,
  GuildCreateResponse,
  GuildInvitationListResponse,
  GuildMembershipMutationResponse,
  GuildPrivateView,
  GuildRelationMutationResponse,
  GuildRelationsResponse,
  GuildResolveInvitationResponse,
  GuildRole,
  GameCommandResponse,
  GameMovementMission,
  GameSnapshot,
  PlayerCrest,
  PlayerShowcase,
  PlayerPortraitUpload,
  PvpAttackInput,
  PvpDefenseFormationInput,
  PublicCitadelDirectory,
  RaceKey,
  ReportReason,
  SocketTicketResponse,
  TacticalPlannerResponse,
  TacticalPlannerStep,
} from "../types";
import type { HeartFrontId } from "../../../cloudflare/src/game/heart-front-definition";
import type { PvpComposition, WarCouncilPreparation } from "../../../cloudflare/src/game/war-council-analysis";
import { parseHeartFrontPublicProjection, type HeartFrontPublicProjection } from "./heart-front-public-projection";
import {
  parseHeartFront1CampPublicProjection,
  type HeartFront1CampPublicProjection,
} from "./heart-front1-camp-public";
import {
  parseHeartChroniclePublicProjection,
  type HeartChroniclePublicProjection,
} from "./heart-chronicle-public-projection";
import {
  parseHeartCentralSnapshot,
  type HeartCentralSnapshot,
} from "./heart-central";
import type { HeartCentralRole } from "../../../cloudflare/src/game/heart-central-runtime";
import type { MapV5RuntimeAccess } from "./map-v5-runtime";

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly diagnostic?: string;
  readonly incidentId?: string;

  constructor(message: string, status: number, code?: string, diagnostic?: string, incidentId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.diagnostic = diagnostic;
    this.incidentId = incidentId;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  timeoutMs?: number;
  idempotencyKey?: string;
}

type TokenProvider = () => Promise<string>;
type Fetcher = typeof fetch;
type AuthenticationExpiredHandler = (error: ApiError) => void;

export function isAuthenticationExpiredSocketClose(
  event: Pick<CloseEvent, "code" | "reason">,
): boolean {
  return event.code === 1008 && event.reason === "authentication_expired";
}

export interface HeartMaintenanceStatus {
  readonly schema: "vaelor-heart-maintenance-status/v1";
  readonly frontId: HeartFrontId;
  readonly state: "held" | "renewable" | "relief-required";
  readonly canRenew: boolean;
  readonly heldUntil: number | null;
  readonly heldFrontCount: number | null;
}

function parseHeartMaintenanceStatus(value: unknown): HeartMaintenanceStatus {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("État de maintien du Cœur invalide.");
  }
  const source = value as Record<string, unknown>;
  if (
    Object.keys(source).sort().join(",") !== "canRenew,frontId,heldFrontCount,heldUntil,schema,state" ||
    source.schema !== "vaelor-heart-maintenance-status/v1" ||
    (source.frontId !== "heart-front-1" && source.frontId !== "heart-front-2" && source.frontId !== "heart-front-3" &&
      source.frontId !== "heart-front-4" && source.frontId !== "heart-front-5" && source.frontId !== "heart-front-6") ||
    (source.state !== "held" && source.state !== "renewable" && source.state !== "relief-required") ||
    typeof source.canRenew !== "boolean" ||
    (source.heldUntil !== null && (!Number.isSafeInteger(source.heldUntil) || Number(source.heldUntil) < 0)) ||
    (source.heldFrontCount !== null && (!Number.isSafeInteger(source.heldFrontCount) || Number(source.heldFrontCount) < 0 || Number(source.heldFrontCount) > 6))
  ) throw new Error("État de maintien du Cœur invalide.");
  return source as unknown as HeartMaintenanceStatus;
}

export class VaelorApi {
  private authenticationExpiryReported = false;

  constructor(
    private readonly baseUrl: string,
    private readonly getToken: TokenProvider,
    private readonly fetcher: Fetcher = fetch,
    private readonly onAuthenticationExpired?: AuthenticationExpiredHandler,
  ) {}

  reportAuthenticationExpired(error = new ApiError(
    "Ta session a expiré. Reconnecte-toi.",
    401,
    "authentication_expired",
  )): void {
    if (this.authenticationExpiryReported) return;
    this.authenticationExpiryReported = true;
    this.onAuthenticationExpired?.(error);
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    if (!path.startsWith("/")) throw new Error("Le chemin API doit commencer par « / ».");
    const token = await this.getToken();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? 15_000);

    try {
      const response = await this.fetcher.call(globalThis, `${this.baseUrl}${path}`, {
        ...options,
        cache: options.cache ?? "no-store",
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        credentials: "same-origin",
        signal: options.signal ?? controller.signal,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          ...(options.body === undefined ? {} : { "Content-Type": "application/json" }),
          ...(options.idempotencyKey ? { "Idempotency-Key": options.idempotencyKey } : {}),
          ...options.headers,
        },
      });
      const payload = await parsePayload(response);
      if (!response.ok) {
        const error = apiErrorFromPayload(response.status, payload);
        if (error.status === 401) {
          try {
            this.reportAuthenticationExpired(error);
          } finally {
            throw error;
          }
        }
        throw error;
      }
      return payload as T;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new ApiError("Le service n’a pas répondu à temps.", 408, "request_timeout");
      }
      const diagnostic = error instanceof Error
        ? `${error.name}: ${error.message}`
        : "Erreur réseau inconnue";
      throw new ApiError(
        "Le portail ne parvient pas à joindre VAELOR.",
        0,
        "network_error",
        diagnostic,
      );
    } finally {
      clearTimeout(timer);
    }
  }

  me(): Promise<AuthMeResponse> {
    return this.request<AuthMeResponse>("/v1/auth/me");
  }

  createGuild(
    identity: GuildCreateInput,
    commandId: string = crypto.randomUUID(),
  ): Promise<GuildCreateResponse> {
    const body = {
      name: identity.name,
      sealCode: identity.sealCode,
      ...(identity.motto ? { motto: identity.motto } : {}),
    } satisfies GuildCreateInput;
    return this.request("/v1/guilds", {
      method: "POST",
      body,
      idempotencyKey: commandId,
    });
  }

  guildMe(): Promise<GuildPrivateView> {
    return this.request<GuildPrivateView>("/v1/guilds/me");
  }

  transferGuildLeadership(
    membershipId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GuildMembershipMutationResponse> {
    return this.request("/v1/guilds/leadership/transfer", {
      method: "POST",
      body: { membershipId },
      idempotencyKey: commandId,
    });
  }

  leaveGuild(commandId: string = crypto.randomUUID()): Promise<GuildMembershipMutationResponse> {
    return this.request("/v1/guilds/leave", {
      method: "POST",
      body: {},
      idempotencyKey: commandId,
    });
  }

  removeGuildMember(
    membershipId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GuildMembershipMutationResponse> {
    return this.request(`/v1/guilds/members/${encodeURIComponent(membershipId)}/remove`, {
      method: "POST",
      body: {},
      idempotencyKey: commandId,
    });
  }

  inviteGuildMember(
    displayName: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GuildCreateInvitationResponse> {
    return this.request("/v1/guilds/invitations", {
      method: "POST",
      body: { displayName },
      idempotencyKey: commandId,
    });
  }

  guildOutgoingInvitations(): Promise<GuildInvitationListResponse> {
    return this.request<GuildInvitationListResponse>("/v1/guilds/invitations/outgoing");
  }

  guildIncomingInvitations(): Promise<GuildInvitationListResponse> {
    return this.request<GuildInvitationListResponse>("/v1/guilds/invitations/me");
  }

  acceptGuildInvitation(
    invitationId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GuildAcceptInvitationResponse> {
    return this.request(`/v1/guilds/invitations/${encodeURIComponent(invitationId)}/accept`, {
      method: "POST",
      body: {},
      idempotencyKey: commandId,
    });
  }

  declineGuildInvitation(
    invitationId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GuildResolveInvitationResponse> {
    return this.request(`/v1/guilds/invitations/${encodeURIComponent(invitationId)}/decline`, {
      method: "POST",
      body: {},
      idempotencyKey: commandId,
    });
  }

  guildRelations(): Promise<GuildRelationsResponse> {
    return this.request<GuildRelationsResponse>("/v1/guilds/relations");
  }

  proposeGuildRelation(
    targetGuildName: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GuildRelationMutationResponse> {
    return this.request("/v1/guilds/relations/propose", {
      method: "POST",
      body: { targetGuildName },
      idempotencyKey: commandId,
    });
  }

  acceptGuildRelation(
    relationId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GuildRelationMutationResponse> {
    return this.mutateGuildRelation(relationId, "accept", commandId);
  }

  declineGuildRelation(
    relationId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GuildRelationMutationResponse> {
    return this.mutateGuildRelation(relationId, "decline", commandId);
  }

  withdrawGuildRelation(
    relationId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GuildRelationMutationResponse> {
    return this.mutateGuildRelation(relationId, "withdraw", commandId);
  }

  dissolveGuildRelation(
    relationId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GuildRelationMutationResponse> {
    return this.mutateGuildRelation(relationId, "dissolve", commandId);
  }

  private mutateGuildRelation(
    relationId: string,
    action: "accept" | "decline" | "withdraw" | "dissolve",
    commandId: string,
  ): Promise<GuildRelationMutationResponse> {
    return this.request(`/v1/guilds/relations/${encodeURIComponent(relationId)}/${action}`, {
      method: "POST",
      body: {},
      idempotencyKey: commandId,
    });
  }

  publicCitadels(): Promise<PublicCitadelDirectory> {
    return this.request("/v1/world/citadels");
  }

  async heartFronts(): Promise<HeartFrontPublicProjection> {
    const payload = await this.request<unknown>("/v1/world/heart/fronts");
    return parseHeartFrontPublicProjection(payload);
  }

  async heartFront1Camp(): Promise<HeartFront1CampPublicProjection> {
    const payload = await this.request<unknown>(
      "/v1/world/heart/fronts/heart-front-1/camps/heart:0:-9",
    );
    return parseHeartFront1CampPublicProjection(payload);
  }

  async heartChronicle(): Promise<HeartChroniclePublicProjection> {
    const payload = await this.request<unknown>("/v1/world/heart/chronicle");
    return parseHeartChroniclePublicProjection(payload);
  }

  async heartCentral(): Promise<HeartCentralSnapshot> {
    const payload = await this.request<unknown>("/v1/game/heart/central", {
      cache: "no-store",
    });
    return parseHeartCentralSnapshot(payload);
  }

  async openHeartCentral(commandId: string = crypto.randomUUID()): Promise<HeartCentralSnapshot> {
    const payload = await this.request<unknown>("/v1/game/heart/central/open", {
      method: "POST",
      body: {},
      idempotencyKey: commandId,
    });
    return parseHeartCentralSnapshot(payload);
  }

  async contributeHeartCentral(
    role: HeartCentralRole,
    commandId: string = crypto.randomUUID(),
  ): Promise<HeartCentralSnapshot> {
    const payload = await this.request<unknown>("/v1/game/heart/central/contribute", {
      method: "POST",
      body: { role },
      idempotencyKey: commandId,
    });
    return parseHeartCentralSnapshot(payload);
  }

  async settleHeartCentral(commandId: string = crypto.randomUUID()): Promise<HeartCentralSnapshot> {
    const payload = await this.request<unknown>("/v1/game/heart/central/settle", {
      method: "POST",
      body: {},
      idempotencyKey: commandId,
    });
    return parseHeartCentralSnapshot(payload);
  }

  gameBootstrap(): Promise<{ snapshot: GameSnapshot }> {
    return this.request("/v1/game/bootstrap");
  }

  getMapV5RuntimeAccess(): MapV5RuntimeAccess {
    return {
      baseUrl: this.baseUrl,
      getToken: () => this.getToken(),
      fetcher: this.fetcher,
      authenticationExpiryReported: () => this.authenticationExpiryReported,
      reportAuthenticationExpired: error => this.reportAuthenticationExpired(error),
    };
  }

  constructionPlan(): Promise<TacticalPlannerResponse> {
    return this.request<TacticalPlannerResponse>("/v1/game/construction-plan", {
      cache: "no-store",
    });
  }

  replaceConstructionPlan(
    expectedRevision: number,
    steps: TacticalPlannerStep[],
    commandId: string = crypto.randomUUID(),
  ): Promise<TacticalPlannerResponse> {
    return this.request<TacticalPlannerResponse>("/v1/game/construction-plan", {
      method: "PUT",
      body: { commandId, expectedRevision, steps },
      idempotencyKey: commandId,
    });
  }

  initializeGame(
    race: RaceKey,
    commandId: string = crypto.randomUUID(),
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/initialize", {
      method: "POST",
      body: { race },
      idempotencyKey: commandId,
    });
  }

  startBuilding(
    buildingKey: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/construction/start", {
      method: "POST",
      body: { buildingKey },
      idempotencyKey: commandId,
    });
  }

  queueBuilding(
    buildingKey: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/construction/queue", {
      method: "POST",
      body: { buildingKey },
      idempotencyKey: commandId,
    });
  }

  cancelQueuedBuilding(commandId: string = crypto.randomUUID()): Promise<GameCommandResponse> {
    return this.request("/v1/game/construction/queue/cancel", {
      method: "POST",
      body: {},
      idempotencyKey: commandId,
    });
  }

  async heartMaintenance(
    frontId: HeartFrontId,
  ): Promise<HeartMaintenanceStatus> {
    return parseHeartMaintenanceStatus(await this.request<unknown>(
      `/v1/game/heart/maintenance?frontId=${encodeURIComponent(frontId)}`,
    ));
  }

  async renewHeartMaintenance(
    frontId: HeartFrontId,
    commandId: string = crypto.randomUUID(),
  ): Promise<{ status: HeartMaintenanceStatus }> {
    const payload = await this.request<{ status: unknown }>("/v1/game/heart/maintenance/renew", {
      method: "POST",
      body: { frontId },
      idempotencyKey: commandId,
    });
    return { status: parseHeartMaintenanceStatus(payload.status) };
  }

  async joinHeartRelief(
    frontId: "heart-front-1" | "heart-front-2",
    commandId: string = crypto.randomUUID(),
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/heart/relief", {
      method: "POST",
      body: { frontId },
      idempotencyKey: commandId,
    });
  }

  armOrder(
    kind: "construction" | "research",
    targetKey: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/order/arm", {
      method: "POST",
      body: { kind, targetKey },
      idempotencyKey: commandId,
    });
  }

  cancelOrder(
    orderId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/order/cancel", {
      method: "POST",
      body: { orderId },
      idempotencyKey: commandId,
    });
  }

  manualStartOrder(
    orderId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/order/manual-start", {
      method: "POST",
      body: { orderId },
      idempotencyKey: commandId,
    });
  }

  startResearch(
    researchKey: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/research/start", {
      method: "POST",
      body: { researchKey },
      idempotencyKey: commandId,
    });
  }

  cancelResearch(commandId: string = crypto.randomUUID()): Promise<GameCommandResponse> {
    return this.request("/v1/game/research/cancel", {
      method: "POST",
      body: {},
      idempotencyKey: commandId,
    });
  }

  startRecruitment(
    unitKey: string,
    quantity: number,
    commandId: string = crypto.randomUUID(),
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/recruitment/start", {
      method: "POST",
      body: { unitKey, quantity },
      idempotencyKey: commandId,
    });
  }

  cancelRecruitment(commandId: string = crypto.randomUUID()): Promise<GameCommandResponse> {
    return this.request("/v1/game/recruitment/cancel", {
      method: "POST",
      body: {},
      idempotencyKey: commandId,
    });
  }

  purchaseDoctrine(
    skillId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/doctrine/purchase", {
      method: "POST",
      body: { skillId },
      idempotencyKey: commandId,
    });
  }

  getPvpTargets(signal?: AbortSignal): Promise<unknown> {
    return this.request<unknown>("/v1/game/pvp/targets", { signal });
  }

  getPvpStatus(signal?: AbortSignal): Promise<unknown> {
    return this.request<unknown>("/v1/game/pvp/status", { signal });
  }

  setPvpDefenseFormation(
    input: PvpDefenseFormationInput,
    commandId: string,
    signal?: AbortSignal,
  ): Promise<unknown> {
    return this.request<unknown>("/v1/game/pvp/defense/formation", {
      method: "POST",
      body: input,
      idempotencyKey: commandId,
      signal,
    });
  }

  startPvpAttack(
    input: PvpAttackInput,
    commandId: string = crypto.randomUUID(),
    signal?: AbortSignal,
  ): Promise<unknown> {
    return this.request<unknown>("/v1/game/pvp/attack", {
      method: "POST",
      body: input,
      idempotencyKey: commandId,
      signal,
    });
  }

  getEspionageStatus(signal?: AbortSignal): Promise<unknown> {
    return this.request<unknown>("/v1/game/espionage", { cache: "no-store", signal });
  }

  dispatchEspionage(
    targetRef: string,
    commandId: string = crypto.randomUUID(),
    signal?: AbortSignal,
  ): Promise<unknown> {
    return this.request<unknown>("/v1/game/espionage/dispatch", {
      method: "POST",
      body: { targetRef },
      cache: "no-store",
      idempotencyKey: commandId,
      signal,
    });
  }

  warCouncilContext(signal?: AbortSignal): Promise<unknown> {
    return this.request<unknown>("/v1/game/war-council", { cache: "no-store", signal });
  }

  analyzeWarCouncil(reportId: string, army: PvpComposition | null, signal?: AbortSignal): Promise<unknown> {
    return this.request<unknown>("/v1/game/war-council/analyze", {
      method: "POST",
      body: { reportId, army },
      cache: "no-store",
      signal,
    });
  }

  saveWarCouncilPreparation(
    expectedRevision: number,
    preparation: Omit<WarCouncilPreparation, "reportObservedAt" | "targetRef" | "savedAt">,
    signal?: AbortSignal,
  ): Promise<unknown> {
    return this.request<unknown>("/v1/game/war-council/preparations", {
      method: "PUT",
      body: { expectedRevision, preparation },
      cache: "no-store",
      signal,
    });
  }

  startMovement(
    destination: { q: number; r: number },
    composition: Record<string, number>,
    mission: Exclude<GameMovementMission, "espionage">,
    commandId: string = crypto.randomUUID(),
    formation?: string,
    frontId?: HeartFrontId,
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/movement/start", {
      method: "POST",
      body: {
        destination,
        composition,
        mission,
        ...(formation ? { formation } : {}),
        ...(frontId ? { frontId } : {}),
      },
      idempotencyKey: commandId,
    });
  }

  recallMovement(
    movementCommandId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/movement/recall", {
      method: "POST",
      body: { movementCommandId },
      idempotencyKey: commandId,
    });
  }

  rallyToHeartFront(
    frontId: "heart-front-3" | "heart-front-4" | "heart-front-5" | "heart-front-6",
    commandId: string = crypto.randomUUID(),
  ): Promise<GameCommandResponse> {
    return this.request("/v1/game/world/rally", {
      method: "POST",
      body: { frontId },
      idempotencyKey: commandId,
    });
  }

    updateProfile(
    displayName: string,
    reengagementEnabled: boolean,
  ): Promise<{ player: AuthMeResponse["player"] }> {
    return this.request("/v1/auth/profile", {
      method: "PUT",
      body: { displayName, reengagementEnabled },
    });
  }

  updateEmailPreferences(
    reengagementEnabled: boolean,
  ): Promise<{ emailPreferences: EmailPreferences }> {
    return this.request("/v1/auth/email-preferences", {
      method: "PUT",
      body: { reengagementEnabled },
    });
  }

  updateCrest(sigil: CrestSigil, palette: CrestPalette): Promise<{ crest: PlayerCrest }> {
    return this.request("/v1/auth/crest", {
      method: "PUT",
      body: { sigil, palette },
    });
  }

  updateShowcase(
    portrait: PlayerShowcase["portrait"],
    frame: PlayerShowcase["frame"],
    nameColor: PlayerShowcase["nameColor"],
  ): Promise<{ showcase: PlayerShowcase }> {
    return this.request("/v1/auth/showcase", {
      method: "PUT",
      body: { portrait, frame, nameColor },
    });
  }

  async uploadShowcaseImage(image: Blob): Promise<PlayerPortraitUpload> {
    const token = await this.getToken();
    const response = await this.fetcher.call(globalThis, `${this.baseUrl}/v1/auth/showcase/image`, {
      method: "PUT",
      cache: "no-store",
      credentials: "same-origin",
      body: image,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "Content-Type": image.type,
      },
    });
    const payload = await parsePayload(response);
    if (!response.ok) {
      throw apiErrorFromPayload(response.status, payload);
    }
    return payload as PlayerPortraitUpload;
  }

  async customShowcaseImage(): Promise<Blob> {
    const token = await this.getToken();
    const response = await this.fetcher.call(globalThis, `${this.baseUrl}/v1/auth/showcase/image`, {
      method: "GET",
      cache: "no-store",
      credentials: "same-origin",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const payload = await parsePayload(response);
      throw apiErrorFromPayload(response.status, payload);
    }
    return response.blob();
  }

  socketTicket(channelId: string): Promise<SocketTicketResponse> {
    return this.request("/v1/auth/socket-ticket", {
      method: "POST",
      body: { kind: "chat", targetId: channelId },
      idempotencyKey: crypto.randomUUID(),
    });
  }

  siegeSocketTicket(roomId: string): Promise<SocketTicketResponse> {
    return this.request("/v1/auth/socket-ticket", {
      method: "POST",
      body: { kind: "siege", targetId: roomId },
      idempotencyKey: crypto.randomUUID(),
    });
  }

  chatHistory(channelId: string, beforeSeq?: number): Promise<ChatHistoryResponse> {
    const query = new URLSearchParams({ limit: "50" });
    if (beforeSeq !== undefined) query.set("beforeSeq", String(beforeSeq));
    return this.request(
      `/v1/chat/channels/${encodeURIComponent(channelId)}/messages?${query.toString()}`,
    );
  }

  reportMessage(
    messageId: string,
    reasonCode: ReportReason,
    detailsText?: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<ChatReportReceipt> {
    return this.request(`/v1/chat/messages/${encodeURIComponent(messageId)}/report`, {
      method: "POST",
      body: { reasonCode, detailsText: detailsText?.trim() || undefined },
      idempotencyKey: commandId,
    });
  }

  setBlocked(
    playerId: string,
    blocked: boolean,
    channelId: string,
    commandId: string = crypto.randomUUID(),
  ): Promise<{ applied: boolean; targetPlayerId: string; effect: string; replayed: boolean }> {
    const action = blocked ? "block" : "unblock";
    return this.request(`/v1/chat/players/${encodeURIComponent(playerId)}/${action}`, {
      method: "POST",
      body: { channelId },
      idempotencyKey: commandId,
    });
  }
}

export function deriveWebSocketBase(apiBase: string): string {
  const url = new URL(apiBase);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString().replace(/\/$/, "");
}

export async function loadGuildCallProjections(
  api: VaelorApi,
  role?: GuildRole,
): Promise<GuildCallProjection> {
  const incomingPromise = api.guildIncomingInvitations();
  const outgoingPromise = role === "leader"
    ? api.guildOutgoingInvitations()
    : Promise.resolve({ invitations: [] });
  const [incoming, outgoing] = await Promise.all([incomingPromise, outgoingPromise]);
  return {
    incoming: incoming.invitations,
    outgoing: outgoing.invitations,
  };
}

export function sendGuildCall(
  api: VaelorApi,
  displayName: string,
  commandId: string,
): Promise<GuildCreateInvitationResponse> {
  return api.inviteGuildMember(displayName, commandId);
}

export function resolveGuildCall(
  api: VaelorApi,
  intent: "accept" | "decline",
  callId: string,
  commandId: string,
): Promise<GuildAcceptInvitationResponse | GuildResolveInvitationResponse> {
  return intent === "accept"
    ? api.acceptGuildInvitation(callId, commandId)
    : api.declineGuildInvitation(callId, commandId);
}

async function parsePayload(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiError("Le service a renvoyé une réponse illisible.", response.status, "invalid_json");
  }
}

function readErrorCode(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return undefined;
  const source = payload as Record<string, unknown>;
  if (typeof source.error === "string") return source.error;
  if (typeof source.code === "string") return source.code;
  return undefined;
}

const INCIDENT_ID_PATTERN = /^vai_[a-f0-9]{32}$/;

function readIncidentId(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return undefined;
  const incidentId = (payload as Record<string, unknown>).incidentId;
  return typeof incidentId === "string" && INCIDENT_ID_PATTERN.test(incidentId)
    ? incidentId
    : undefined;
}

function apiErrorFromPayload(status: number, payload: unknown): ApiError {
  const code = readErrorCode(payload);
  const incidentId = readIncidentId(payload);
  const message = apiErrorMessage(status, code);
  const playerMessage = incidentId
    ? `${message} Référence support : ${incidentId}.`
    : message;
  return new ApiError(playerMessage, status, code, undefined, incidentId);
}

export function apiErrorMessage(status: number, code?: string): string {
  const known: Record<string, string> = {
    account_not_active: "Ce compte ne peut pas accéder au monde.",
    chat_access_denied: "Tu n’as pas accès à ce salon.",
    chat_message_not_found: "Ce message n’existe plus.",
    chat_message_not_reportable: "Ce message n’est plus visible dans ton Courrier.",
    chat_report_already_submitted: "Ce message a déjà été signalé depuis ton compte.",
    chat_report_command_conflict: "Cette tentative ne correspond plus au signalement conservé.",
    chat_report_unavailable: "Le reçu du signalement n’est pas disponible. Réessaie sans modifier le formulaire.",
    report_invalid: "Choisis un motif valide et limite les précisions à 1 000 caractères.",
    cannot_block_self: "Tu ne peux pas te bloquer toi-même.",
    cannot_report_own_message: "Tu ne peux pas signaler ton propre message.",
    display_name_already_chosen: "Ce pseudonyme de Seigneur a déjà été confirmé.",
    display_name_invalid: "Ce pseudonyme ne respecte pas les règles de VAELOR.",
    display_name_unavailable: "Ce pseudonyme est déjà pris.",
    email_contact_unavailable: "Aucune adresse vérifiée n’est liée à ce compte.",
    email_preference_invalid: "Le choix de relance est invalide.",
    email_verification_required: "Vérifie ton adresse e-mail avant d’entrer dans VAELOR.",
    profile_incomplete: "Confirme d’abord ton pseudonyme de Seigneur.",
    guild_name_conflict: "Ce nom de Compagnie est déjà pris dans ce monde.",
    guild_membership_exists: "Tu appartiens déjà à une Compagnie.",
    idempotency_key_required: "La clé de commande est manquante. Réessaie.",
    idempotency_key_invalid: "La clé de commande est invalide. Réessaie.",
    idempotency_key_conflict: "Cette tentative de fondation ne correspond plus au brouillon conservé.",
    game_heart_front_unavailable: "Ce Front reste scellé pour ta progression actuelle.",
    guild_identity_invalid: "Les informations de la Compagnie sont invalides. Vérifie les champs puis réessaie.",
    guild_creation_conflict: "La fondation de la Compagnie est entrée en conflit. Réessaie.",
    guild_not_found: "Aucune Compagnie active n’est disponible pour ce profil.",
    guild_invitation_request_invalid: "Le pseudonyme exact est nécessaire pour cet appel.",
    guild_target_not_found: "Ce pseudonyme ne peut pas être appelé dans ce monde.",
    guild_target_self: "Tu ne peux pas t’appeler toi-même.",
    guild_target_already_member: "Ce Seigneur appartient déjà à une Compagnie.",
    guild_invitation_exists: "Un appel est déjà actif pour ce pseudonyme.",
    guild_invitation_not_found: "Cet appel n’est plus disponible.",
    guild_invitation_expired: "Cet appel a expiré. Relis les appels reçus.",
    guild_invitation_resolved: "Cet appel a déjà été traité. Relis les appels reçus.",
    guild_invitation_forbidden: "Cet appel ne t’est pas destiné.",
    guild_invitation_limit_reached: "La Compagnie ne peut pas appeler davantage de Seigneurs pour le moment.",
    guild_invitation_rate_limited: "Trop d’appels pour le moment. Attends un peu avant de réessayer.",
    guild_invitation_conflict: "Cet appel a changé entre-temps. Relis les appels reçus.",
    guild_leader_required: "Seul le Porte-Bannière peut gérer les membres de la Compagnie.",
    guild_member_not_found: "Ce Compagnon n’est plus dans la Compagnie. Relis la liste.",
    guild_membership_inactive: "Cette adhésion n’est plus active. Relis la liste.",
    guild_member_forbidden: "Cette action n’est pas autorisée pour ce Compagnon.",
    guild_leadership_transfer_required: "Transmets d’abord la bannière à un Compagnon avant de quitter la Compagnie.",
    guild_last_leader_invalid: "La Compagnie ne peut pas être archivée dans cet état. Relis la liste.",
    guild_membership_conflict: "La Compagnie a changé entre-temps. Relis la liste avant de réessayer.",
    guild_relation_request_invalid: "Le nom exact de la Compagnie est nécessaire pour ce pacte.",
    guild_relation_target_not_found: "Cette Compagnie ne peut pas être jointe dans ce monde.",
    guild_relation_target_self: "Ta Compagnie ne peut pas conclure un pacte avec elle-même.",
    guild_relation_not_found: "Ce pacte n’est plus disponible. Relis les alliances.",
    guild_relation_forbidden: "Ce pacte ne peut pas être traité par ta Compagnie.",
    guild_relation_conflict: "Ce pacte a changé entre-temps. Relis les alliances.",
    network_error: "Le portail ne parvient pas à joindre VAELOR.",
    unknown_error: "Une erreur inattendue empêche cette action. Réessaie.",
    game_race_invalid: "Ce peuple n’existe pas dans VAELOR.",
    game_not_initialized: "Prête d’abord le Serment du Bastion.",
    game_already_initialized: "Le Serment de ce Bastion est déjà scellé.",
    game_construction_busy: "Un chantier est déjà en cours.",
    game_construction_queue_requires_active: "L’ordre manuel suivant exige un chantier actif.",
    game_construction_queue_busy: "Un seul ordre global peut être préparé.",
    game_construction_queue_empty: "Aucun ordre manuel suivant n’est préparé.",
    game_construction_queue_level_conflict: "L’ordre préparé ne correspond plus à l’état du Bastion.",
    game_construction_queue_economy_version_unsupported: "L’ordre préparé attend une mise à jour du Bastion.",
    game_construction_queue_refund_capacity: "L’ancien ordre réservé ne peut pas encore être migré sans perte.",
    game_resources_insufficient: "Tes réserves ne suffisent pas pour ce chantier.",
    game_building_not_authoritative: "Ce bâtiment n’est pas encore géré par le serveur.",
    game_doctrine_skill_sealed: "Cette voie de Doctrine est encore scellée.",
    game_doctrine_skill_not_authoritative: "Cette voie n’appartient pas à ton Peuple.",
    game_doctrine_skill_max_level: "Cette voie de Doctrine a atteint son niveau maximal.",
    game_doctrine_level_required: "Ton Renom ne permet pas encore ce serment.",
    game_doctrine_marks_insufficient: "Tu ne possèdes pas assez de Marques du Serment.",
    game_espionage_request_invalid: "La demande d’espionnage est invalide. Relis le Bastion choisi.",
    game_espionage_target_unavailable: "Ce Bastion n’est plus disponible pour une mission d’espionnage. Actualise les Bastions reconnus.",
    game_espionage_scout_unavailable: "Aucun Éclaireur disponible ne peut être réservé pour cette mission.",
    game_espionage_already_active: "Une mission d’espionnage est déjà active pour ce monde.",
    game_espionage_unavailable: "Le service d’espionnage est temporairement indisponible. Relis l’état, puis reprends la même tentative.",
    tactical_planner_entitlement_required: "Le Planificateur nécessite un droit actif. Ton plan reste lisible et exportable.",
    war_council_entitlement_required: "Le Pacte du Sénéchal est inactif. Les rapports et préparations restent lisibles ; l’analyse et l’enregistrement sont désactivés.",
    war_council_report_required: "Ce rapport n’est plus disponible. Relis les rapports du Conseil.",
    war_council_report_stale: "La limite de ce rapport est atteinte. Il reste consultable, mais ne peut plus être analysé.",
    war_council_report_invalid: "Ce rapport ne peut pas être validé. Actualise le Conseil.",
    war_council_army_invalid: "Les effectifs disponibles ont changé. Actualise le Conseil avant de continuer.",
    war_council_revision_conflict: "Les préparations ont changé depuis leur lecture. Actualise le Conseil avant d’enregistrer.",
    war_council_unavailable: "Le Conseil de guerre est temporairement indisponible.",
    tactical_planner_revision_conflict: "Le plan a changé entre-temps. Relis-le avant de réessayer.",
    tactical_planner_replay_conflict: "Cette commande ne correspond plus au brouillon conservé.",
    tactical_planner_command_invalid: "La commande du plan est invalide. Réessaie.",
    tactical_planner_request_invalid: "Le plan envoyé est invalide. Vérifie les champs puis réessaie.",
    tactical_planner_steps_invalid: "Une intention du plan est invalide.",
    tactical_planner_step_invalid: "Une intention du plan est invalide.",
    tactical_planner_intent_duplicate: "Chaque intention du plan doit avoir un identifiant différent.",
    tactical_planner_steps_limit: "Un plan ne peut pas dépasser cinquante intentions.",
    tactical_planner_snapshot_invalid: "L’état du Bastion est incomplet ; le plan n’a pas été calculé.",
    tactical_planner_snapshot_unavailable: "L’état du Bastion n’est pas disponible ; rien n’a été modifié.",
    tactical_planner_prerequisite_unmet: "Une intention demande un prérequis qui n’est pas satisfait dans cet ordre.",
    tactical_planner_building_unavailable: "Ce bâtiment n’est pas disponible pour ce Bastion.",
    tactical_planner_projection_invalid: "La projection du plan n’est pas calculable ; rien n’a été modifié.",
    tactical_planner_economy_version_unsupported: "Le plan attend une mise à jour des règles du Bastion.",
    tactical_planner_plan_corrupt: "Le plan conservé est incohérent ; aucune mutation n’a été effectuée.",
    tactical_planner_receipt_corrupt: "Le reçu conservé est incohérent ; aucune mutation n’a été effectuée.",
    tactical_planner_unavailable: "Le Planificateur est momentanément indisponible.",
    command_id_conflict: "Cette commande a déjà servi pour une autre action.",
    event_delivery_unavailable: "L’action est conservée, mais son journal attend le serveur.",
    scope_required: "Ta session n’autorise pas encore cette action.",
  };
  if (code && known[code]) return known[code];
  if (code?.startsWith("scope_required:")) return known.scope_required;
  if (status === 401) return "Ta session a expiré. Reconnecte-toi.";
  if (status === 403) return "Cette action ne t’est pas autorisée.";
  if (status === 404) return "Cette fonction n’est pas disponible.";
  if (status === 409) return "L’action entre en conflit avec un état plus récent.";
  if (status === 429) return "Trop de demandes. Attends un instant.";
  if (status >= 500) return "VAELOR rencontre un incident temporaire.";
  return "La demande a été refusée.";
}
